import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { aiApi, type EnvelopeAnalysis } from '@/api/ai';
import {
  aiRefusal,
  ErrorCodes,
  isAiWalletRefusal,
  isApiError,
  type AiRefusal,
  type AiWalletReason,
} from '@/api/errors';
import { aiDisabledCopy } from '@/api/errorCopy';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { TOP_UP_TITLE_ID, useTopUpSwap, type TopUpSwap } from '@/features/aiWallet/useTopUpSwap';
import { AiJobFailure } from './AiJobFailure';
import { AiPill } from '@/components/AiPill';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';

const POLL_MS = 2000;
const MAX_MS = 60_000;

interface AnalysisDialogProps {
  envelopeId: number;
  from?: string;
  to?: string;
  onClose: () => void;
}

type View =
  | { status: 'running' }
  | { status: 'ready'; analysis: EnvelopeAnalysis }
  | { status: 'timeout'; id: number }
  | { status: 'error'; message: string }
  | { status: 'wallet'; refusal: AiRefusal & { reason: AiWalletReason } };

async function pollUntilDone(
  envelopeId: number,
  seed: EnvelopeAnalysis,
): Promise<EnvelopeAnalysis | { timedOut: true; id: number }> {
  const start = Date.now();
  let current = seed;
  while (current.status === 'PENDING' && Date.now() - start < MAX_MS) {
    await new Promise((resolve) => {
      window.setTimeout(resolve, POLL_MS);
    });
    current = await aiApi.getAnalysis(envelopeId, current.id);
  }
  if (current.status === 'PENDING') return { timedOut: true, id: current.id };
  return current;
}

export function AnalysisDialog({ envelopeId, from, to, onClose }: AnalysisDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [view, setView] = useState<View>({ status: 'running' });
  const [previous, setPrevious] = useState<EnvelopeAnalysis[]>([]);
  // Try again after buying AI credit asks once more; nothing reruns on its own (MM-D-131).
  const [attempt, setAttempt] = useState(0);
  const { swap: topUpSwap, swapped: toppingUp, attachPanelHost, requestHostClose } = useTopUpSwap();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const created = await aiApi.requestAnalysis(envelopeId, {
          ...(from !== undefined ? { from } : {}),
          ...(to !== undefined ? { to } : {}),
        });
        const done =
          created.status === 'PENDING' ? await pollUntilDone(envelopeId, created) : created;
        if (cancelled) return;
        if ('timedOut' in done) setView({ status: 'timeout', id: done.id });
        else setView({ status: 'ready', analysis: done });
      } catch (err) {
        if (cancelled) return;
        const refusal = aiRefusal(err);
        if (isAiWalletRefusal(refusal)) {
          setView({ status: 'wallet', refusal });
        } else if (refusal !== null) {
          setView({ status: 'error', message: aiDisabledCopy(t, refusal) });
        } else if (isApiError(err) && err.code === ErrorCodes.AI_BUDGET_EXCEEDED) {
          setView({ status: 'error', message: t('ai.capped') });
        } else {
          setView({ status: 'error', message: t('imports.errorGeneric') });
        }
      }
      try {
        const list = await aiApi.listAnalyses(envelopeId);
        if (!cancelled) setPrevious(list);
      } catch {
        /* previous list is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt, from, t, to, envelopeId]);

  async function handleRecheck(id: number) {
    setView({ status: 'running' });
    try {
      const current = await aiApi.getAnalysis(envelopeId, id);
      const done =
        current.status === 'PENDING' ? await pollUntilDone(envelopeId, current) : current;
      if ('timedOut' in done) setView({ status: 'timeout', id: done.id });
      else setView({ status: 'ready', analysis: done });
    } catch {
      setView({ status: 'error', message: t('imports.errorGeneric') });
    }
  }

  return (
    <Dialog
      open
      // While the top-up shows, the backdrop and Esc are its Cancel (TMMAPP-0119 item 16).
      onClose={() => requestHostClose(onClose)}
      fullWidth
      maxWidth="sm"
      aria-labelledby={toppingUp ? TOP_UP_TITLE_ID : 'analysis-title'}
    >
      {/* TOP UP swaps this body for the top-up in place (DLG-005, TMMAPP-0118 C11). */}
      <Box sx={{ display: toppingUp ? 'none' : 'contents' }}>
        {/* TMM-B9: the analysis endpoint is model-produced; the pill sits beside the title. */}
        <DialogTitle
          id="analysis-title-row"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
        >
          <span id="analysis-title">{t('ai.analysisTitle')}</span>
          <AiPill />
        </DialogTitle>
        <DialogContent>
          {view.status === 'running' && <Typography>{t('common.loading')}</Typography>}
          {view.status === 'error' && <Alert severity="warning">{view.message}</Alert>}
          {view.status === 'wallet' && (
            <AiWalletPrompt
              refusal={view.refusal}
              onTryAgain={() => {
                setView({ status: 'running' });
                setAttempt((n) => n + 1);
              }}
              onCloseHost={onClose}
              swap={topUpSwap}
            />
          )}
          {view.status === 'timeout' && (
            <Alert
              severity="info"
              action={
                <Button color="inherit" size="small" onClick={() => void handleRecheck(view.id)}>
                  {t('ai.recheck')}
                </Button>
              }
            >
              {t('ai.stillRunning')}
            </Alert>
          )}
          {view.status === 'ready' && (
            <AnalysisBody
              analysis={view.analysis}
              locale={locale}
              onClose={onClose}
              swap={topUpSwap}
              onRetry={() => {
                setView({ status: 'running' });
                setAttempt((n) => n + 1);
              }}
            />
          )}
          {previous.length > 0 && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" component="h3">
                {t('ai.previousAnalyses')}
              </Typography>
              {previous.map((item) => (
                <Typography key={item.id} variant="body2">
                  {item.from !== null ? formatCivilDate(item.from, locale) : '—'} –{' '}
                  {item.to !== null ? formatCivilDate(item.to, locale) : '—'} ·{' '}
                  {t(`ai.analysisStatus.${item.status}`)}
                </Typography>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <DialogDoneButton onClick={onClose} />
        </DialogActions>
      </Box>
      <Box ref={attachPanelHost} sx={{ display: 'contents' }} data-top-up-host="" />
    </Dialog>
  );
}

function AnalysisBody({
  analysis,
  locale,
  onRetry,
  onClose,
  swap,
}: {
  analysis: EnvelopeAnalysis;
  locale: string;
  onRetry: () => void;
  /** The dialog's close: a wallet prompt's subscribe card closes it first (DLG-005). */
  onClose: () => void;
  /** The dialog's body swap: a wallet prompt's TOP UP replaces the body in place (C11). */
  swap: TopUpSwap;
}) {
  const { t } = useTranslation();
  if (analysis.status === 'FAILED') {
    // The reader asked for this analysis on their own wallet: Analyze is the owner's (MM-D-47).
    return (
      <AiJobFailure
        failureReason={analysis.failureReason}
        firstPurchaseRequired={analysis.firstPurchaseRequired}
        debtMinor={analysis.debtMinor}
        walletOwner
        fallback={t('ai.analysisFailed')}
        onRetry={onRetry}
        onCloseHost={onClose}
        swap={swap}
      />
    );
  }
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('ai.period')}
        {': '}
        {formatCivilDate(analysis.from, locale)} – {formatCivilDate(analysis.to, locale)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {t('ai.manifestNote')}
      </Typography>
      {analysis.body !== null && <Typography>{analysis.body}</Typography>}
      {analysis.findings.map((finding, index) => (
        <Stack key={index} spacing={0.5}>
          <Typography variant="subtitle2" component="h3">
            {finding.kind !== null ? t(`ai.findingKind.${finding.kind}`) : t('ai.analysisTitle')}
            {finding.month !== null ? ` · ${finding.month}` : ''}
          </Typography>
          <Typography variant="body2">{finding.text}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}
